import { useEffect, useMemo, useState, useRef } from "react";
import {
  Table,
  Tag,
  Button,
  Input,
  Space,
  Card,
  Modal,
  Toast,
  Form,
  Typography,
  RadioGroup,
  Radio,
  Divider,
} from "@douyinfe/semi-ui";
import { IconSearch, IconDownload, IconUpload, IconAlertTriangle, IconClose } from "@douyinfe/semi-icons";
import { StatCard } from "../../comp/StatCard";
import { ApiClient } from "../../api/ApiClient";
import {
  ContractApi,
  ContractListItem,
  ContractStat,
  ContractQuery,
  ContractCreateReq,
  ContractFileItem,
} from "../../api/contract";
import dayjs from "dayjs";

// Semi UI Tag color union (not exported from root, mirrored here)
type TagColor =
  | "amber" | "blue" | "cyan" | "green" | "grey" | "indigo"
  | "light-blue" | "light-green" | "lime" | "orange" | "pink"
  | "purple" | "red" | "teal" | "violet" | "yellow" | "white";

const CHANNEL_COLORS: Record<string, TagColor> = {
  QIANCHENG: "blue",
  MEITUAN: "orange",
  ELEME: "pink",
};
const DEFAULT_CHANNEL_COLOR: TagColor = "grey";

function channelColor(code?: string): TagColor {
  if (!code) return DEFAULT_CHANNEL_COLOR;
  return CHANNEL_COLORS[code.toUpperCase()] ?? DEFAULT_CHANNEL_COLOR;
}

function dateStatusColor(ds: ContractListItem["dateStatus"]): TagColor {
  switch (ds) {
    case "ACTIVE": return "green";
    case "EXPIRING": return "yellow";
    case "EXPIRED": return "red";
    case "INVALID": return "grey";
  }
}

function dateStatusLabel(ds: ContractListItem["dateStatus"], remainingDays: number) {
  switch (ds) {
    case "ACTIVE": return `${remainingDays}天`;
    case "EXPIRING": return `${remainingDays}天`;
    case "EXPIRED": return "已过期";
    case "INVALID": return "无效";
  }
}

function formatSize(bytes?: number) {
  if (!bytes) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

interface TrafficAppOption { id: number; name: string; code: string; }

export default function ContractPage() {
  const [list, setList] = useState<ContractListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [stat, setStat] = useState<ContractStat>({ total: 0, active: 0, expiring: 0, expired: 0 });
  const [query, setQuery] = useState<ContractQuery>({ page: 1, pageSize: 20 });
  const [keyword, setKeyword] = useState("");
  const [channels, setChannels] = useState<TrafficAppOption[]>([]);
  const [allChannels, setAllChannels] = useState<TrafficAppOption[]>([]);
  const [loading, setLoading] = useState(false);
  // Task 10 will replace with real modal state
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<ContractListItem | null>(null);
  // const [formApi, setFormApi] = useState<any>(undefined);
  const formApi = useRef<any>();
  // 已选合同附件列表（上传成功/编辑回填），支持多文件
  const [pickedFiles, setPickedFiles] = useState<ContractFileItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [expiringChips, setExpiringChips] = useState<ContractListItem[]>([]);

  const apiClient = ApiClient.getDefault();

  async function fetchChannels() {
    try {
      const resp = await ContractApi.channels();
      setChannels((resp.data ?? []).map((c) => ({
        id: c.trafficAppId,
        name: c.trafficAppName ?? `#${c.trafficAppId}`,
        code: c.trafficAppCode ?? "",
      })));
    } catch (e) {
      // 沉默失败，渠道筛选用空列表
    }
  }

  async function fetchAllChannels() {
    try {
      const resp = await apiClient.request<any[]>("/trafficApps/allTrafficApps", "GET");
      setAllChannels((resp.data ?? []).map((c: any) => ({
        id: c.id,
        name: c.name ?? `#${c.id}`,
        code: c.code ?? "",
      })));
    } catch (e) {
      // 沉默失败
    }
  }

  async function fetchList() {
    setLoading(true);
    try {
      const resp = await ContractApi.list(query);
      setList(resp.data?.records ?? []);
      setTotal(resp.data?.total ?? 0);
    } finally {
      setLoading(false);
    }
  }

  async function fetchStat() {
    const resp = await ContractApi.stat();
    if (resp.data) setStat(resp.data);
  }

  async function fetchExpiringChips() {
    try {
      const resp = await ContractApi.list({ status: "EXPIRING", page: 1, pageSize: 10 });
      setExpiringChips(resp.data?.records ?? []);
    } catch (e) {
      setExpiringChips([]);
    }
  }

  useEffect(() => { fetchChannels(); fetchAllChannels(); }, []);
  useEffect(() => {
    fetchList();
    fetchStat();
    fetchExpiringChips();
  }, [query]);

  function setFilter(partial: Partial<ContractQuery>) {
    setQuery({ ...query, ...partial, page: 1 });
  }

  function onSearch() {
    setFilter({ keyword: keyword || undefined });
  }

  function onEdit(r: ContractListItem) {
    setEditing(r);
    setPickedFiles(r.files ? r.files.map((f) => ({ ...f })) : []);
    // formApi is set via <Form getFormApi={...}> ref callback, so values can be set after a tick
    setTimeout(() => {
      formApi.current?.setValues({
        id: r.id,
        storeName: r.storeName,
        trafficAppId: r.trafficAppId,
        contractType: r.contractType,
        // 起止时间仅写入已注册的 dateRange 字段；onOk 会统一从 dateRange 读取。
        dateRange: [r.startDate, r.endDate],
        status: r.status,
        remark: r.remark ?? "",
      });
    }, 500);
    setEditOpen(true);
  }
  async function onDownload(r: ContractListItem) {
    try {
      const resp = await ContractApi.getDownloadUrl(r.id);
      if (resp.data) window.open(resp.data, "_blank");
    } catch (e: any) {
      Toast.error(e?.message ?? "获取下载链接失败");
    }
  }
  async function onDownloadFile(fileId: number) {
    try {
      const resp = await ContractApi.getDownloadFileUrl(fileId);
      if (resp.data) window.open(resp.data, "_blank");
    } catch (e: any) {
      Toast.error(e?.message ?? "获取下载链接失败");
    }
  }
  async function onDelete(r: ContractListItem) {
    Modal.confirm({
      title: "确认删除",
      content: `删除合同「${r.storeName}」？文件也会从 OSS 删除。`,
      onOk: async () => {
        await ContractApi.remove(r.id);
        Toast.success("已删除");
        fetchList();
        fetchStat();
      },
    });
  }

  const columns = useMemo(() => [
    {
      title: "门店名称",
      dataIndex: "storeName",
      width: 200,
      render: (v: string, r: ContractListItem) => (
        <div>
          <div style={{ fontSize: 14, color: "var(--semi-color-text-0)", fontWeight: 500 }}>{v}</div>
          {r.remark && (
            <div style={{ fontSize: 12, color: "var(--semi-color-text-2)", marginTop: 2 }}>{r.remark}</div>
          )}
        </div>
      ),
    },
    {
      title: "渠道",
      dataIndex: "trafficAppName",
      width: 100,
      render: (v: string, r: ContractListItem) => (
        <Tag color={channelColor(r.trafficAppCode)} size="small" shape="circle">{v}</Tag>
      ),
    },
    {
      title: "类型",
      dataIndex: "contractType",
      width: 100,
      render: (v: string) => (
        <Tag size="small" shape="circle" type="light">
          {v === "ELECTRONIC" ? "电子合同" : "纸质合同"}
        </Tag>
      ),
    },
    {
      title: "合同文件",
      dataIndex: "files",
      width: 260,
      render: (_: any, r: ContractListItem) => {
        const files = r.files ?? [];
        if (!files.length) return <Typography.Text type="tertiary">-</Typography.Text>;
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {files.map((f, i) => (
              <a
                key={f.id ?? f.filePath ?? i}
                onClick={() => (f.id ? onDownloadFile(f.id) : onDownload(r))}
                style={{ fontSize: 13, display: "inline-flex", alignItems: "center", gap: 4 }}
              >
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 170 }}>
                  {f.fileName}
                </span>
                <Tag size="small" color="grey" shape="circle">{formatSize(f.fileSize)}</Tag>
              </a>
            ))}
          </div>
        );
      },
    },
    {
      title: "起止时间",
      width: 200,
      render: (_: any, r: ContractListItem) => {
        const days = dayjs(r.endDate).diff(dayjs(r.startDate), "day");
        return (
          <div>
            <div style={{ fontSize: 13, color: "var(--semi-color-text-0)" }}>
              {dayjs(r.startDate).format("YYYY/MM/DD")} — {dayjs(r.endDate).format("YYYY/MM/DD")}
            </div>
            <div style={{ fontSize: 12, color: "var(--semi-color-text-2)" }}>共 {days} 天</div>
          </div>
        );
      },
    },
    {
      title: "剩余天数",
      width: 110,
      render: (_: any, r: ContractListItem) => (
        <Tag color={dateStatusColor(r.dateStatus)} size="small" shape="circle">
          {dateStatusLabel(r.dateStatus, r.remainingDays)}
        </Tag>
      ),
    },
    {
      title: "操作",
      width: 200,
      render: (_: any, r: ContractListItem) => (
        <Space spacing={0}>
          <Button theme="borderless" size="small" onClick={() => onEdit(r)}>编辑</Button>
          <Divider layout="vertical" />
          <Button theme="borderless" size="small" onClick={() => onDownload(r)}>下载</Button>
          {(r.dateStatus === "EXPIRED" || r.status === 0) && (
            <>
              <Divider layout="vertical" />
              <Button theme="borderless" type="danger" size="small" onClick={() => onDelete(r)}>删除</Button>
            </>
          )}
        </Space>
      ),
    },
  ], []);

  const statCards: Array<{
    key: undefined | "ACTIVE" | "EXPIRING" | "EXPIRED";
    label: string;
    value: number;
    accent: "neutral" | "success" | "warning" | "danger";
    hint?: React.ReactNode;
    unit?: string;
  }> = [
    { key: undefined, label: "总合同", value: stat.total, accent: "neutral" },
    {
      key: "ACTIVE",
      label: "有效",
      value: stat.active,
      accent: "success",
      hint: stat.total > 0 ? `占比 ${((stat.active / stat.total) * 100).toFixed(1)}%` : undefined,
    },
    {
      key: "EXPIRING",
      label: "即将到期",
      value: stat.expiring,
      accent: "warning",
      hint: "≤30 天",
    },
    {
      key: "EXPIRED",
      label: "已过期",
      value: stat.expired,
      accent: "danger",
      hint: stat.total > 0 ? `占比 ${((stat.expired / stat.total) * 100).toFixed(1)}%` : undefined,
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 16 }}>
        <div>
          <Typography.Title heading={4} style={{ margin: 0 }}>合同管理</Typography.Title>
          <Typography.Text type="tertiary" size="small">
            管理门店合作合同的到期与续签 · 共 {stat.total} 份
            {stat.expiring > 0 && (
              <>
                {" · "}
                <Typography.Text type="warning" size="small">即将到期 {stat.expiring} 份</Typography.Text>
              </>
            )}
          </Typography.Text>
        </div>
        <Space>
          <Button icon={<IconDownload />} theme="light" onClick={async () => {
            try {
              await ContractApi.exportExcel(query);
              Toast.success("导出成功");
            } catch (e: any) {
              Toast.error(e?.message ?? "导出失败");
            }
          }}>导出</Button>
          <Button icon={<IconUpload />} theme="solid" type="primary"
            onClick={() => { setEditing(null); setPickedFiles([]); formApi.current?.reset(); setEditOpen(true); }}>
            上传合同
          </Button>
        </Space>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 16 }}>
        {statCards.map((c) => (
          <StatCard
            key={c.label}
            label={c.label}
            value={c.value}
            unit={c.unit}
            hint={c.hint}
            accent={c.accent}
            selected={query.status === c.key}
            onClick={() => setFilter({ status: c.key as ContractQuery["status"], bizStatus: undefined })}
          />
        ))}
      </div>

      {expiringChips.length > 0 && (
        <div
          style={{
            marginBottom: 16,
            padding: "10px 14px",
            borderRadius: 8,
            background: "var(--semi-color-warning-light-default)",
            border: "1px solid var(--semi-color-warning)",
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <IconAlertTriangle style={{ color: "var(--semi-color-warning)" }} />
          <Typography.Text strong style={{ color: "var(--semi-color-warning)" }}>
            近期到期 {expiringChips.length} 份：
          </Typography.Text>
          <Space spacing={8} wrap>
            {expiringChips.map((c) => (
              <Tag
                key={c.id}
                size="small"
                color="orange"
                shape="circle"
                type="light"
                onClick={() => setFilter({ status: "EXPIRING" })}
                style={{ cursor: "pointer" }}
              >
                {c.storeName} · {c.remainingDays}天
              </Tag>
            ))}
          </Space>
        </div>
      )}

      <Card style={{ marginBottom: 16 }} bodyStyle={{ padding: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
          <Typography.Text strong style={{ width: 40 }}>状态</Typography.Text>
          <RadioGroup
            type="pureCard"
            value={query.bizStatus === 0 ? "INVALID" : query.status ?? "ALL"}
            onChange={(e) => {
              const v = e.target.value as string;
              if (v === "ALL") setFilter({ status: undefined, bizStatus: undefined });
              else if (v === "INVALID") setFilter({ bizStatus: 0, status: undefined });
              else setFilter({ status: v as any, bizStatus: undefined });
            }}
          >
            <Radio value="ALL">全部</Radio>
            <Radio value="ACTIVE">有效</Radio>
            <Radio value="EXPIRING">即将到期</Radio>
            <Radio value="EXPIRED">已过期</Radio>
            <Radio value="INVALID">无效</Radio>
          </RadioGroup>
        </div>
        <Divider margin={12} />
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
          <Typography.Text strong style={{ width: 40 }}>渠道</Typography.Text>
          <RadioGroup
            type="pureCard"
            value={query.trafficAppId ?? "ALL"}
            onChange={(e) => {
              const v = e.target.value;
              setFilter({ trafficAppId: v === "ALL" ? undefined : Number(v) });
            }}
          >
            <Radio value={"ALL"}>全部</Radio>
            {channels.map((ch) => (
              <Radio key={ch.id} value={ch.id}>{ch.name}</Radio>
            ))}
          </RadioGroup>
        </div>
        <Divider margin={12} />
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Typography.Text strong style={{ width: 40 }}>搜索</Typography.Text>
          <Input
            placeholder="搜索门店名称"
            value={keyword}
            onChange={setKeyword}
            onEnterPress={onSearch}
            prefix={<IconSearch />}
            style={{ width: 280 }}
          />
          <Button theme="solid" onClick={onSearch}>搜索</Button>
        </div>
      </Card>

      <Table
        columns={columns as any}
        dataSource={list}
        rowKey="id"
        loading={loading}
        pagination={{
          currentPage: query.page ?? 1,
          pageSize: query.pageSize ?? 20,
          total,
          onPageChange: (p) => setQuery({ ...query, page: p }),
          onPageSizeChange: (ps) => setQuery({ ...query, page: 1, pageSize: ps }),
          showSizeChanger: true,
          pageSizeOpts: [10, 20, 50, 100],
        }}
        bordered
        size="middle"
        empty={<Typography.Text type="tertiary">暂无合同数据</Typography.Text>}
      />

      <Modal
        title={editing ? "编辑合同" : "上传合同"}
        visible={editOpen}
        onCancel={() => { setEditOpen(false); setEditing(null); setPickedFiles([]); formApi.current?.reset(); }}
        onOk={async () => {
          const values: any = formApi.current?.getValues() ?? {};
          if (!values.storeName ) {
            Toast.error("请补全门店");
            return;
          }
          if (!values.trafficAppId ) {
            Toast.error("请补全渠道");
            return;
          }
          // 直接从已注册的 dateRange 字段读取起止时间。
          // 注意：startDate/endDate 并非已注册字段，Semi 的 setValues 只覆盖已注册字段，
          // 因此编辑时 setValues 写入的 startDate/endDate 会被忽略，getValues 取不到，
          // 进而误报“请补全日期”。统一从 dateRange 读取即可同时覆盖新增/编辑两种场景。
          const dateRange: any[] = Array.isArray(values.dateRange) ? values.dateRange : [];
          const startDate = dateRange[0];
          const endDate = dateRange[1];
          if (!startDate || !endDate) {
            Toast.error("请补全日期");
            return;
          }
          if (!pickedFiles.length) {
            Toast.error("请至少上传一个合同文件");
            return;
          }
          const files = pickedFiles.map((f) => ({
            id: f.id,
            fileName: f.fileName,
            filePath: f.filePath,
            fileSize: f.fileSize,
          }));
          try {
            if (editing) {
              await ContractApi.update({
                id: editing.id,
                storeName: values.storeName,
                trafficAppId: Number(values.trafficAppId),
                contractType: values.contractType ?? "ELECTRONIC",
                files,
                startDate: dayjs(startDate).format("YYYY-MM-DD"),
                endDate: dayjs(endDate).format("YYYY-MM-DD"),
                status: values.status ?? 1,
                remark: values.remark,
              });
              Toast.success("已更新");
            } else {
              const req: ContractCreateReq = {
                storeName: values.storeName,
                trafficAppId: Number(values.trafficAppId),
                contractType: values.contractType ?? "ELECTRONIC",
                files,
                startDate: dayjs(startDate).format("YYYY-MM-DD"),
                endDate: dayjs(endDate).format("YYYY-MM-DD"),
                status: values.status ?? 1,
                remark: values.remark,
              };
              await ContractApi.create(req);
              Toast.success("已上传");
            }
            setEditOpen(false);
            setEditing(null);
            setPickedFiles([]);
            formApi.current?.reset();
            fetchList();
            fetchStat();
          } catch (e: any) {
            Toast.error(e?.message ?? "保存失败");
          }
        }}
      >
        <Form getFormApi={(api)=>{ formApi.current = api; }} labelPosition="left">
          <Form.Input field="storeName" label="门店名称" rules={[{ required: true, message: "必填" }]} />
          <Form.Select filter field="trafficAppId" label="渠道" style={{ width: "100%" }}
            rules={[{ required: true, message: "必填" }]}>
            {allChannels.map((ch) => (
              <Form.Select.Option key={ch.id} value={ch.id}>{ch.name}</Form.Select.Option>
            ))}
          </Form.Select>
          <Form.RadioGroup field="contractType" defaultValue={"ELECTRONIC"} label="合同类型" rules={[{ required: true, message: "必填" }]} options={[
            { value: "ELECTRONIC", label: "电子合同" },
            { value: "PAPER", label: "纸质合同" },
          ]} />
          <Form.DatePicker type="dateRange" field="dateRange" label="起止时间" rules={[{ required: true, message: "必填" }]} />
          <Form.Slot label={<span>合同文件 <span style={{ color: "var(--semi-color-danger)" }}>*</span></span>}>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.docx"
              style={{ display: "none" }}
              onChange={async (e) => {
                const selected = Array.from(e.target.files ?? []);
                e.target.value = ""; // 允许重复选择同一文件
                if (!selected.length) return;
                setUploading(true);
                try {
                  for (const f of selected) {
                    try {
                      const resp = await ContractApi.upload(f);
                      setPickedFiles((prev) => [...prev, {
                        fileName: resp.data!.fileName,
                        filePath: resp.data!.filePath,
                        fileSize: resp.data!.fileSize,
                      }]);
                    } catch (err: any) {
                      Toast.error(`${f.name} 上传失败：${err?.message ?? ""}`);
                    }
                  }
                } finally {
                  setUploading(false);
                }
              }}
            />
            <Space wrap>
              {pickedFiles.map((f, i) => (
                <Tag
                  key={f.filePath ?? i}
                  size="large"
                  color="light-blue"
                  shape="circle"
                  style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 10px" }}
                >
                  <span style={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {f.fileName}
                  </span>
                  <span style={{ color: "var(--semi-color-text-2)", fontSize: 12 }}>{formatSize(f.fileSize)}</span>
                  <IconClose
                    style={{ cursor: "pointer", color: "var(--semi-color-text-2)" }}
                    onClick={() => setPickedFiles((prev) => prev.filter((_, idx) => idx !== i))}
                  />
                </Tag>
              ))}
              <Button icon={<IconUpload />} loading={uploading} theme="light"
                onClick={() => fileInputRef.current?.click()}>
                添加文件
              </Button>
            </Space>
            <Typography.Text type="tertiary" size="small" style={{ display: "block", marginTop: 4 }}>
              支持多文件，单个不超过 50MB（PDF/JPG/PNG/DOCX）
            </Typography.Text>
          </Form.Slot>
          <Form.Input field="remark" label="备注" />
          <Form.RadioGroup field="status" defaultValue={1} label="状态" rules={[{ required: true, message: "必填" }]} options={[
            { value: 1, label: "有效" },
            { value: 0, label: "无效" },
          ]} />
        </Form>
      </Modal>
    </div>
  );
}
